import React, { useEffect, useMemo, useState } from "react";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import { FormSelect } from "@/components/Base/Form";
import Litepicker from "@/components/Base/Litepicker";
import Lucide from "@/components/Base/Lucide";
import { getToken } from "@/utils/auth";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import Table from "@/components/Base/Table";
import Chart from "@/components/Base/Chart";
import type { ChartData } from "chart.js/auto";

type PriceBy = "Chain" | "SKU" | "Outlet";

type OutletGroup = {
    id: string;
    groupName: string;
    companyId?: string;
    createdAt?: string;
    updatedAt?: string;
};

type Product = {
    id: string;
    name: string;
    productLine?: string | null;
    productCompetitor?: string[];
};

type HeinekenPriceRow = {
    id: string;
    createdAt: string;
    productId: string | null;
    productName: string | null;
    productLine: string | null;
    price: number | null;
    outlet?: {
        id?: string;
        name?: string;
        outletGroup?: string | null;
        outletGroupRel?: {
            id: string;
            groupName: string;
        } | null;
    } | null;
};

type PricingTableRow = {
    ownProductId: string;
    ownProductName: string;
    ownPrice: number | null;
    competitorProductId: string | null;
    competitorProductName: string | null;
    competitorPrice: number | null;
};

type OutletGroupPriceStat = {
    outletGroupId: string;
    outletGroupName: string;
    price: number;
};

type Outlet = {
    id: string;
    name: string;
    outletGroup?: string | null;
    companyId?: string | null;
    projectId?: string | null;
    status?: boolean;
};

function buildChartLabels(rows: PricingTableRow[]) {
    return rows.map((r) => {
        const competitor = r.competitorProductName || "Competitor";
        return `${r.ownProductName} vs ${competitor}`;
    });
}

function buildBarChartData(rows: PricingTableRow[]): ChartData<"bar"> {
    const labels = buildChartLabels(rows);
    return {
        labels,
        datasets: [
            {
                label: "Own",
                data: rows.map((r) => r.ownPrice),
                backgroundColor: "rgba(0, 122, 61, 0.75)", // Heineken green
                borderColor: "#007A3D",
                borderWidth: 1,
                borderRadius: 4,
                // Smaller categoryPercentage => more gap between products (categories)
                // Larger barPercentage => less gap between green/red bars within the same category
                barPercentage: 0.95,
                categoryPercentage: 0.58,
                barThickness: 10,
                maxBarThickness: 12,
                order: 1,
            },
            {
                label: "Competitor",
                data: rows.map((r) => r.competitorPrice),
                backgroundColor: "rgba(220, 38, 38, 0.70)", // red
                borderColor: "#DC2626",
                borderWidth: 1,
                borderRadius: 4,
                barPercentage: 0.95,
                categoryPercentage: 0.58,
                barThickness: 10,
                maxBarThickness: 12,
                order: 2,
            },
        ],
    };
}

function buildBarChartOptions(): any {
    return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        layout: {
            padding: {
                top: 12,
                bottom: 12,
            },
        },
        plugins: {
            legend: {
                position: "top",
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const v = ctx.parsed?.x;
                        if (v === null || v === undefined) return `${ctx.dataset.label}: -`;
                        return `${ctx.dataset.label}: RM ${Number(v).toFixed(2)}`;
                    },
                },
            },
        },
        scales: {
            x: {
                ticks: {
                    font: { size: 9 },
                    padding: 2,
                    maxTicksLimit: 5,
                    // Keep ticks compact so the chart fits the container nicely
                    callback: (value) => `${value}`,
                },
                grid: ({
                    color: "rgba(148, 163, 184, 0.35)", // slate-400-ish
                    borderDash: [2, 2],
                } as any),
                border: { display: false },
                stacked: false,
            },
            y: {
                // Add spacing so the first/last bars aren't clipped
                offset: true,
                ticks: {
                    font: { size: 10 },
                    autoSkip: false,
                    padding: 6,
                    callback: function (value: any) {
                        const label = this.getLabelForValue(value);
                        if (typeof label !== "string") return label;

                        // Wrap long labels onto multiple lines instead of truncating with "..."
                        const wrap = (text: string, maxLen: number) => {
                            const words = text.split(/\s+/).filter(Boolean);
                            const lines: string[] = [];
                            let current = "";
                            for (const w of words) {
                                // If a single word is extremely long, hard-split it
                                if (w.length > maxLen) {
                                    if (current) {
                                        lines.push(current);
                                        current = "";
                                    }
                                    for (let i = 0; i < w.length; i += maxLen) {
                                        lines.push(w.slice(i, i + maxLen));
                                    }
                                    continue;
                                }
                                const next = current ? `${current} ${w}` : w;
                                if (next.length > maxLen) {
                                    if (current) lines.push(current);
                                    current = w;
                                } else {
                                    current = next;
                                }
                            }
                            if (current) lines.push(current);
                            return lines;
                        };

                        // Prefer a nice 2-line structure: own name then "vs competitor"
                        const parts = label.split(" vs ");
                        const own = parts[0] || label;
                        const competitor = parts[1] ? `vs ${parts[1]}` : "";

                        const ownLines = wrap(own, 28);
                        const compLines = competitor ? wrap(competitor, 28) : [];
                        return [...ownLines, ...compLines];
                    },
                },
                grid: { display: false },
                border: { display: false },
                stacked: false,
            },
        },
    };
}

function getBarChartHeightPx(itemCount: number) {
    // Horizontal bars: height should scale with number of categories
    // Increase per-item height to add more vertical gap between products
    // Thinner bars => we can use a smaller per-item height to reduce overall chart height
    // Slightly taller to accommodate multi-line Y-axis labels
    return Math.max(300, itemCount * 40 + 120);
}

function buildSkuOutletGroupChartData(params: {
    outletGroups: OutletGroup[];
    priceRows: HeinekenPriceRow[];
    ownProductId: string;
    competitorProductId: string | null;
}): ChartData<"bar"> | null {
    const { outletGroups, priceRows, ownProductId, competitorProductId } = params;

    // Helper to get all prices for a product in a given outlet group
    const pricesFor = (productId: string, outletGroupId: string) => {
        return priceRows
            .filter(
                (r) =>
                    r.productId === productId &&
                    r.price !== null &&
                    r.outlet?.outletGroupRel?.id === outletGroupId
            )
            .map((r) => r.price as number);
    };

    // Build series per outlet group and ALWAYS show all outlet groups on the X-axis.
    const labels: string[] = outletGroups.map((g) => g.groupName);
    const ownValues: Array<number | null> = outletGroups.map((g) =>
        pickModeOrMedian(pricesFor(ownProductId, g.id))
    );
    const competitorValues: Array<number | null> = outletGroups.map((g) => {
        if (!competitorProductId) return null;
        return pickModeOrMedian(pricesFor(competitorProductId, g.id));
    });

    if (labels.length === 0) return null;

    return {
        labels,
        datasets: [
            {
                label: "Own",
                data: ownValues,
                backgroundColor: "rgba(0, 122, 61, 0.75)",
                borderColor: "#007A3D",
                borderWidth: 1,
                borderRadius: 4,
                barThickness: 8,
                maxBarThickness: 10,
            },
            {
                label: "Competitor",
                data: competitorValues,
                backgroundColor: "rgba(220, 38, 38, 0.70)",
                borderColor: "#DC2626",
                borderWidth: 1,
                borderRadius: 4,
                barThickness: 8,
                maxBarThickness: 10,
            },
        ],
    };
}

function getProductOutletGroupStats(params: {
    outletGroups: OutletGroup[];
    priceRows: HeinekenPriceRow[];
    productId: string;
}): {
    high: OutletGroupPriceStat | null;
    low: OutletGroupPriceStat | null;
} {
    const { outletGroups, priceRows, productId } = params;

    const pricesFor = (outletGroupId: string) => {
        return priceRows
            .filter(
                (r) =>
                    r.productId === productId &&
                    r.price !== null &&
                    r.outlet?.outletGroupRel?.id === outletGroupId
            )
            .map((r) => r.price as number);
    };

    const stats: OutletGroupPriceStat[] = [];
    outletGroups.forEach((g) => {
        const representative = pickModeOrMedian(pricesFor(g.id));
        if (representative === null) return;
        stats.push({
            outletGroupId: g.id,
            outletGroupName: g.groupName,
            price: representative,
        });
    });

    if (stats.length === 0) return { high: null, low: null };

    // Deterministic tie-break: first encountered in outletGroups order (already preserved in stats push order)
    let high = stats[0];
    let low = stats[0];
    for (const s of stats) {
        if (s.price > high.price) high = s;
        if (s.price < low.price) low = s;
    }

    return { high, low };
}

function buildSkuOutletGroupChartOptions(): any {
    return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                bottom: 24,
            },
        },
        plugins: {
            legend: {
                position: "top",
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const v = ctx.parsed?.y;
                        if (v === null || v === undefined) return `${ctx.dataset.label}: -`;
                        return `${ctx.dataset.label}: RM ${Number(v).toFixed(2)}`;
                    },
                },
            },
        },
        scales: {
            x: {
                ticks: {
                    font: { size: 9 },
                    // Show ALL outlet groups on the axis (no skipping)
                    autoSkip: false,
                    maxRotation: 60,
                    minRotation: 60,
                },
                grid: { display: false },
                border: { display: false },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    font: { size: 10 },
                    maxTicksLimit: 6,
                    callback: (value: any) => `RM ${value}`,
                },
                grid: ({
                    color: "rgba(148, 163, 184, 0.35)",
                    borderDash: [2, 2],
                } as any),
                border: { display: false },
            },
        },
    };
}

function isOwnProductLine(productLine: string | null | undefined) {
    const v = (productLine || "").toLowerCase().trim();
    // Database default is "Own"
    return v === "" || v.startsWith("own");
}

function isCompetitorProductLine(productLine: string | null | undefined) {
    const v = (productLine || "").toLowerCase().trim();
    return v.startsWith("competitor");
}

function pickModeOrMedian(prices: number[]): number | null {
    const normalized = prices
        .filter((p) => typeof p === "number" && Number.isFinite(p))
        // treat prices as 2dp for grouping (currency)
        .map((p) => Number(p.toFixed(2)));

    if (normalized.length === 0) return null;

    // Mode
    const counts = new Map<number, number>();
    for (const p of normalized) counts.set(p, (counts.get(p) || 0) + 1);

    let maxCount = 0;
    for (const c of counts.values()) maxCount = Math.max(maxCount, c);

    if (maxCount > 1) {
        const candidates: number[] = [];
        for (const [p, c] of counts.entries()) {
            if (c === maxCount) candidates.push(p);
        }
        // Deterministic tie-breaker: pick lowest price among the modes
        return candidates.sort((a, b) => a - b)[0] ?? null;
    }

    // No mode → pick the median value (a “central” observed price, without averaging)
    const sorted = [...normalized].sort((a, b) => a - b);
    return sorted[Math.floor((sorted.length - 1) / 2)] ?? null;
}

function formatRM(value: number | null) {
    if (value === null || value === undefined) return "-";
    return value.toFixed(2);
}

function formatDiffRM(own: number | null, competitor: number | null) {
    if (own === null || competitor === null) return "-";
    const diff = own - competitor;
    return diff.toFixed(2);
}

function getPriceIndexPercentValue(own: number | null, competitor: number | null) {
    if (own === null || competitor === null || competitor === 0) return null;
    return ((own - competitor) / competitor) * 100;
}

function formatPriceIndexPercent(value: number | null) {
    if (value === null || value === undefined) return "-";
    return `${value.toFixed(1)}%`;
}

export default function Pricing() {
    const { projectId } = useParams<{ projectId: string }>();
    const [priceBy, setPriceBy] = useState<PriceBy>("Chain");
    const [date, setDate] = useState(() => dayjs().format("YYYY-MM-DD"));

    const [outletGroups, setOutletGroups] = useState<OutletGroup[]>([]);
    const [loadingOutletGroups, setLoadingOutletGroups] = useState(false);
    const [outlets, setOutlets] = useState<Outlet[]>([]);
    const [loadingOutlets, setLoadingOutlets] = useState(false);
    const [expandedOutletGroupIds, setExpandedOutletGroupIds] = useState<Set<string>>(
        () => new Set()
    );
    const [expandPrefsLoaded, setExpandPrefsLoaded] = useState(false);
    const [savedKnownOutletGroupIds, setSavedKnownOutletGroupIds] = useState<string[] | null>(
        null
    );

    const [expandedSkuCardIds, setExpandedSkuCardIds] = useState<Set<string>>(
        () => new Set()
    );
    const [skuExpandPrefsLoaded, setSkuExpandPrefsLoaded] = useState(false);
    const [savedKnownSkuCardIds, setSavedKnownSkuCardIds] = useState<string[] | null>(null);

    const [productsById, setProductsById] = useState<Map<string, Product>>(
        () => new Map()
    );
    const [productOrderIndexById, setProductOrderIndexById] = useState<Map<string, number>>(
        () => new Map()
    );
    const [loadingProducts, setLoadingProducts] = useState(false);

    const [priceRows, setPriceRows] = useState<HeinekenPriceRow[]>([]);
    const [loadingPrices, setLoadingPrices] = useState(false);

    const apiUrl = useMemo(
        () => import.meta.env.VITE_API_URL || "http://localhost:4000",
        []
    );

    const companyIdFromQuery = useMemo(() => {
        try {
            return new URLSearchParams(window.location.search).get("companyId");
        } catch {
            return null;
        }
    }, []);

    // Load products (for competitor mapping) - scoped by projectId
    useEffect(() => {
        if (!projectId) return;

        const controller = new AbortController();

        (async () => {
            try {
                setLoadingProducts(true);
                const allProducts: Product[] = [];
                let page = 1;
                let totalPages = 1;

                while (page <= totalPages) {
                    const params = new URLSearchParams();
                    params.append("page", String(page));
                    params.append("pageSize", "100");
                    // Sort is done server-side via displayOrder then createdAt
                    const res = await fetch(`${apiUrl}/products?${params.toString()}`, {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    });
                    const json = await res.json();
                    const pageData: any[] = Array.isArray(json?.data) ? json.data : [];
                    totalPages = Number(json?.totalPages) || 1;
                    page += 1;

                    pageData.forEach((p) => {
                        allProducts.push({
                            id: p.id,
                            name: p.name,
                            productLine: p.productLine ?? null,
                            productCompetitor: Array.isArray(p.productCompetitor)
                                ? p.productCompetitor
                                : [],
                        });
                    });

                    // Safety: prevent accidental long loops
                    if (page > 50) break;
                }

                const map = new Map<string, Product>();
                allProducts.forEach((p) => map.set(p.id, p));
                setProductsById(map);

                // Preserve the same order as Products page: backend already sorts by displayOrder then newest.
                const orderMap = new Map<string, number>();
                allProducts.forEach((p, idx) => {
                    orderMap.set(p.id, idx);
                });
                setProductOrderIndexById(orderMap);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch products:", err);
                setProductsById(new Map());
                setProductOrderIndexById(new Map());
            } finally {
                setLoadingProducts(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, projectId]);

    // Load HeinekenPrice rows for selected date - scoped by projectId
    useEffect(() => {
        if (!projectId) return;
        if (!date || !date.trim()) return;

        const controller = new AbortController();

        (async () => {
            try {
                setLoadingPrices(true);
                const startDate = dayjs(date).startOf("day").toISOString();
                const endDate = dayjs(date).endOf("day").toISOString();

                const params = new URLSearchParams();
                params.append("projectId", projectId);
                params.append("startDate", startDate);
                params.append("endDate", endDate);
                params.append("page", "1");
                params.append("pageSize", "10000");

                const res = await fetch(
                    `${apiUrl}/heineken-prices?${params.toString()}`,
                    {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    }
                );
                const json = await res.json();
                const rows: any[] = Array.isArray(json?.data) ? json.data : [];

                setPriceRows(
                    rows.map((r) => ({
                        id: r.id,
                        createdAt: r.createdAt,
                        productId: r.productId ?? null,
                        productName: r.productName ?? null,
                        productLine: r.productLine ?? null,
                        price: typeof r.price === "number" ? r.price : null,
                        outlet: r.outlet ?? null,
                    }))
                );
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch Heineken prices:", err);
                setPriceRows([]);
            } finally {
                setLoadingPrices(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, date, projectId]);

    useEffect(() => {
        if (priceBy !== "Chain" && priceBy !== "SKU" && priceBy !== "Outlet") return;

        const controller = new AbortController();

        (async () => {
            try {
                setLoadingOutletGroups(true);
                const queryParams = new URLSearchParams();
                queryParams.append("page", "1");
                queryParams.append("pageSize", "100");
                queryParams.append("sortBy", "groupName");
                queryParams.append("sortOrder", "asc");

                const res = await fetch(
                    `${apiUrl}/outlet-groups?${queryParams.toString()}`,
                    {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    }
                );

                const json = await res.json();
                const firstPageGroups: OutletGroup[] = Array.isArray(json?.data)
                    ? json.data
                    : [];
                const totalPages = Number(json?.totalPages) || 1;

                // Fetch remaining pages if needed (backend caps pageSize)
                const allGroups = [...firstPageGroups];
                for (let page = 2; page <= totalPages; page++) {
                    const qp = new URLSearchParams(queryParams);
                    qp.set("page", String(page));
                    const resp = await fetch(
                        `${apiUrl}/outlet-groups?${qp.toString()}`,
                        {
                            signal: controller.signal,
                            headers: { Authorization: `Bearer ${getToken()}` },
                        }
                    );
                    const j = await resp.json();
                    const pageGroups: OutletGroup[] = Array.isArray(j?.data)
                        ? j.data
                        : [];
                    allGroups.push(...pageGroups);
                    if (page > 50) break;
                }

                setOutletGroups(allGroups);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch outlet groups:", err);
                setOutletGroups([]);
            } finally {
                setLoadingOutletGroups(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, priceBy]);

    // Load outlets (used for Price By: Outlet table)
    useEffect(() => {
        if (priceBy !== "Outlet") return;

        const controller = new AbortController();

        (async () => {
            try {
                setLoadingOutlets(true);
                const all: Outlet[] = [];
                let page = 1;
                let totalPages = 1;

                while (page <= totalPages) {
                    const params = new URLSearchParams();
                    if (companyIdFromQuery) params.append("companyId", companyIdFromQuery);
                    params.append("page", String(page));
                    params.append("pageSize", "100");
                    params.append("sortBy", "name");
                    params.append("sortOrder", "asc");

                    const res = await fetch(`${apiUrl}/outlets?${params.toString()}`, {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    });
                    const json = await res.json();
                    const pageData: any[] = Array.isArray(json?.data) ? json.data : [];
                    totalPages = Number(json?.totalPages) || 1;
                    page += 1;

                    pageData.forEach((o) => {
                        all.push({
                            id: o.id,
                            name: o.name,
                            outletGroup: o.outletGroup ?? null,
                            companyId: o.companyId ?? null,
                            projectId: o.projectId ?? null,
                            status: typeof o.status === "boolean" ? o.status : undefined,
                        });
                    });

                    // Safety: prevent accidental long loops
                    if (page > 200) break;
                }

                setOutlets(all);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch outlets:", err);
                setOutlets([]);
            } finally {
                setLoadingOutlets(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, priceBy, companyIdFromQuery]);

    const outletGroupExpandStorageKey = useMemo(() => {
        return projectId ? `pricing_outletgroups_expanded_${projectId}` : null;
    }, [projectId]);

    const skuCardExpandStorageKey = useMemo(() => {
        return projectId ? `pricing_sku_cards_expanded_${projectId}` : null;
    }, [projectId]);

    const viewPrefsStorageKey = useMemo(() => {
        return projectId ? `pricing_view_prefs_${projectId}` : null;
    }, [projectId]);

    // Restore Price By + Date (per project)
    useEffect(() => {
        if (!viewPrefsStorageKey) return;
        try {
            const raw = localStorage.getItem(viewPrefsStorageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;

            const pb = (parsed as any).priceBy;
            if (pb === "Chain" || pb === "SKU" || pb === "Outlet") {
                setPriceBy(pb);
            }
            const d = (parsed as any).date;
            if (typeof d === "string" && d.trim()) {
                setDate(d);
            }
        } catch {
            // ignore
        }
    }, [viewPrefsStorageKey]);

    // Persist Price By + Date (per project)
    useEffect(() => {
        if (!viewPrefsStorageKey) return;
        try {
            localStorage.setItem(
                viewPrefsStorageKey,
                JSON.stringify({
                    v: 1,
                    priceBy,
                    date,
                })
            );
        } catch {
            // ignore
        }
    }, [viewPrefsStorageKey, priceBy, date]);

    // Restore expand/collapse state from localStorage (per project)
    useEffect(() => {
        if (!outletGroupExpandStorageKey) return;
        try {
            const raw = localStorage.getItem(outletGroupExpandStorageKey);
            if (!raw) {
                setSavedKnownOutletGroupIds(null);
                setExpandPrefsLoaded(true);
                return;
            }

            const parsed = JSON.parse(raw);
            // New format: { v: 1, expanded: string[], known: string[] }
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                const expanded = Array.isArray((parsed as any).expanded)
                    ? (parsed as any).expanded.filter((x: any) => typeof x === "string")
                    : [];
                const known = Array.isArray((parsed as any).known)
                    ? (parsed as any).known.filter((x: any) => typeof x === "string")
                    : null;

                setExpandedOutletGroupIds(new Set(expanded));
                setSavedKnownOutletGroupIds(known);
                setExpandPrefsLoaded(true);
                return;
            }

            // Legacy format: string[]
            if (Array.isArray(parsed)) {
                setExpandedOutletGroupIds(new Set(parsed.filter((x) => typeof x === "string")));
                // No known snapshot available in legacy format
                setSavedKnownOutletGroupIds(null);
                setExpandPrefsLoaded(true);
            }
        } catch (e) {
            // ignore corrupted values
            setSavedKnownOutletGroupIds(null);
            setExpandPrefsLoaded(true);
        }
    }, [outletGroupExpandStorageKey]);

    // Restore expand/collapse state for SKU cards (per project)
    useEffect(() => {
        if (!skuCardExpandStorageKey) return;
        try {
            const raw = localStorage.getItem(skuCardExpandStorageKey);
            if (!raw) {
                setSavedKnownSkuCardIds(null);
                setSkuExpandPrefsLoaded(true);
                return;
            }

            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                const expanded = Array.isArray((parsed as any).expanded)
                    ? (parsed as any).expanded.filter((x: any) => typeof x === "string")
                    : [];
                const known = Array.isArray((parsed as any).known)
                    ? (parsed as any).known.filter((x: any) => typeof x === "string")
                    : null;
                setExpandedSkuCardIds(new Set(expanded));
                setSavedKnownSkuCardIds(known);
                setSkuExpandPrefsLoaded(true);
                return;
            }

            if (Array.isArray(parsed)) {
                setExpandedSkuCardIds(new Set(parsed.filter((x) => typeof x === "string")));
                setSavedKnownSkuCardIds(null);
                setSkuExpandPrefsLoaded(true);
            }
        } catch (e) {
            setSavedKnownSkuCardIds(null);
            setSkuExpandPrefsLoaded(true);
        }
    }, [skuCardExpandStorageKey]);

    // Default: expand all outlet groups once loaded; keep user toggles for existing IDs
    useEffect(() => {
        if (!expandPrefsLoaded) return;
        if (outletGroups.length === 0) return;
        setExpandedOutletGroupIds((prev) => {
            const next = new Set(prev);
            const currentIds = new Set(outletGroups.map((g) => g.id));
            // Remove groups that no longer exist
            Array.from(next).forEach((id) => {
                if (!currentIds.has(id)) next.delete(id);
            });

            // If no saved state exists at all, default to expanded for all groups
            if (prev.size === 0 && savedKnownOutletGroupIds === null) {
                outletGroups.forEach((g) => next.add(g.id));
                return next;
            }

            // If we have a known snapshot, expand only truly new groups (not previously known)
            if (Array.isArray(savedKnownOutletGroupIds)) {
                const knownSet = new Set(savedKnownOutletGroupIds);
                outletGroups.forEach((g) => {
                    if (!knownSet.has(g.id)) {
                        next.add(g.id);
                    }
                });
            }

            return next;
        });
    }, [outletGroups, expandPrefsLoaded, savedKnownOutletGroupIds]);

    const toggleOutletGroup = (groupId: string) => {
        setExpandedOutletGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    // Persist expand/collapse state
    useEffect(() => {
        if (!outletGroupExpandStorageKey) return;
        try {
            localStorage.setItem(
                outletGroupExpandStorageKey,
                JSON.stringify({
                    v: 1,
                    expanded: Array.from(expandedOutletGroupIds),
                    known: outletGroups.map((g) => g.id),
                })
            );
        } catch (e) {
            // ignore storage errors (e.g., private mode)
        }
    }, [expandedOutletGroupIds, outletGroupExpandStorageKey, outletGroups]);

    // Persist expand/collapse state for SKU cards
    useEffect(() => {
        if (!skuCardExpandStorageKey) return;
        try {
            localStorage.setItem(
                skuCardExpandStorageKey,
                JSON.stringify({
                    v: 1,
                    expanded: Array.from(expandedSkuCardIds),
                    known: null, // filled below once skuCards computed
                })
            );
        } catch (e) {
            // ignore storage errors
        }
    }, [expandedSkuCardIds, skuCardExpandStorageKey]);

    const pricingTableByOutletGroupId = useMemo(() => {
        const byGroup = new Map<string, PricingTableRow[]>();

        // Group price rows by outletGroupId
        const pricesByGroup = new Map<string, HeinekenPriceRow[]>();
        for (const row of priceRows) {
            const groupId = row.outlet?.outletGroupRel?.id;
            if (!groupId) continue;
            if (!pricesByGroup.has(groupId)) pricesByGroup.set(groupId, []);
            pricesByGroup.get(groupId)!.push(row);
        }

        for (const [groupId, rows] of pricesByGroup.entries()) {
            const ownRows = rows.filter(
                (r) => r.productId && isOwnProductLine(r.productLine) && r.price !== null
            );
            const competitorRows = rows.filter(
                (r) =>
                    r.productId &&
                    isCompetitorProductLine(r.productLine) &&
                    r.price !== null
            );

            // Map productId -> prices[]
            const ownPricesByProduct = new Map<string, number[]>();
            for (const r of ownRows) {
                if (!r.productId || r.price === null) continue;
                if (!ownPricesByProduct.has(r.productId))
                    ownPricesByProduct.set(r.productId, []);
                ownPricesByProduct.get(r.productId)!.push(r.price);
            }

            const competitorPricesByProduct = new Map<string, number[]>();
            for (const r of competitorRows) {
                if (!r.productId || r.price === null) continue;
                if (!competitorPricesByProduct.has(r.productId))
                    competitorPricesByProduct.set(r.productId, []);
                competitorPricesByProduct.get(r.productId)!.push(r.price);
            }

            const tableRows: PricingTableRow[] = [];

            for (const [ownProductId, prices] of ownPricesByProduct.entries()) {
                const ownProduct =
                    productsById.get(ownProductId) || (null as any);
                const ownProductName =
                    ownProduct?.name ||
                    ownRows.find((r) => r.productId === ownProductId)
                        ?.productName ||
                    "Unknown";

                const competitorIds: string[] = Array.isArray(
                    ownProduct?.productCompetitor
                )
                    ? ownProduct.productCompetitor
                    : [];
                const competitorProductId =
                    competitorIds.length > 0 ? competitorIds[0] : null;
                const competitorProduct =
                    competitorProductId ? productsById.get(competitorProductId) : null;
                const competitorProductName =
                    competitorProduct?.name ||
                    (competitorProductId
                        ? competitorRows.find(
                              (r) => r.productId === competitorProductId
                          )?.productName || null
                        : null);

                const ownPrice = pickModeOrMedian(prices);
                const competitorPrice =
                    competitorProductId && competitorPricesByProduct.has(competitorProductId)
                        ? pickModeOrMedian(
                              competitorPricesByProduct.get(competitorProductId) || []
                          )
                        : null;

                tableRows.push({
                    ownProductId,
                    ownProductName,
                    ownPrice,
                    competitorProductId,
                    competitorProductName,
                    competitorPrice,
                });
            }

            // Stable sort by product name
            tableRows.sort((a, b) => {
                const aIdx = productOrderIndexById.get(a.ownProductId);
                const bIdx = productOrderIndexById.get(b.ownProductId);
                if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx;
                if (aIdx !== undefined) return -1;
                if (bIdx !== undefined) return 1;
                return (a.ownProductName || "").localeCompare(b.ownProductName || "");
            });

            byGroup.set(groupId, tableRows);
        }

        return byGroup;
    }, [priceRows, productsById, productOrderIndexById]);

    const outletsByGroupName = useMemo(() => {
        const m = new Map<string, Outlet[]>();
        outlets.forEach((o) => {
            const g = o.outletGroup || "";
            if (!g) return;
            if (!m.has(g)) m.set(g, []);
            m.get(g)!.push(o);
        });
        return m;
    }, [outlets]);

    const pricesByOutletIdProductId = useMemo(() => {
        const m = new Map<string, Map<string, number[]>>();
        for (const r of priceRows) {
            const outletId = r.outlet?.id;
            const productId = r.productId;
            if (!outletId || !productId) continue;
            if (r.price === null) continue;

            let byProduct = m.get(outletId);
            if (!byProduct) {
                byProduct = new Map<string, number[]>();
                m.set(outletId, byProduct);
            }
            const arr = byProduct.get(productId) || [];
            arr.push(r.price);
            byProduct.set(productId, arr);
        }
        return m;
    }, [priceRows]);

    const skuCards = useMemo(() => {
        // Build cards from the selected date's price rows so we only show relevant SKUs.
        // One card per own product (competitor derived from product.productCompetitor[0]).
        const ownProductIds = new Set<string>();
        priceRows.forEach((r) => {
            if (!r.productId) return;
            if (!isOwnProductLine(r.productLine)) return;
            ownProductIds.add(r.productId);
        });

        const cards = Array.from(ownProductIds).map((ownId) => {
            const ownProduct = productsById.get(ownId) || null;
            const ownName =
                ownProduct?.name ||
                priceRows.find((r) => r.productId === ownId)?.productName ||
                "Unknown Product";

            const competitorId =
                ownProduct?.productCompetitor && ownProduct.productCompetitor.length > 0
                    ? ownProduct.productCompetitor[0]
                    : null;
            const competitorProduct = competitorId ? productsById.get(competitorId) : null;
            const competitorName =
                competitorProduct?.name ||
                (competitorId
                    ? priceRows.find((r) => r.productId === competitorId)?.productName || null
                    : null) ||
                "Competitor";

            const id = competitorId ? `${ownId}__${competitorId}` : `${ownId}__none`;

            return {
                id,
                ownProductId: ownId,
                ownName,
                competitorProductId: competitorId,
                competitorName,
                title: `${ownName} vs ${competitorName}`,
            };
        });

        // Sort by the same order used in Products page (displayOrder then newest)
        cards.sort((a, b) => {
            const aIdx = productOrderIndexById.get(a.ownProductId);
            const bIdx = productOrderIndexById.get(b.ownProductId);
            if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx;
            if (aIdx !== undefined) return -1;
            if (bIdx !== undefined) return 1;
            return (a.ownName || "").localeCompare(b.ownName || "");
        });
        return cards;
    }, [priceRows, productsById, productOrderIndexById]);

    const skuOwnPriceStatsByCardId = useMemo(() => {
        const map = new Map<
            string,
            { high: OutletGroupPriceStat | null; low: OutletGroupPriceStat | null }
        >();
        skuCards.forEach((card) => {
            map.set(
                card.id,
                getProductOutletGroupStats({
                    outletGroups,
                    priceRows,
                    productId: card.ownProductId,
                })
            );
        });
        return map;
    }, [skuCards, outletGroups, priceRows]);

    const skuCompetitorPriceStatsByCardId = useMemo(() => {
        const map = new Map<
            string,
            { high: OutletGroupPriceStat | null; low: OutletGroupPriceStat | null }
        >();
        skuCards.forEach((card) => {
            if (!card.competitorProductId) {
                map.set(card.id, { high: null, low: null });
                return;
            }
            map.set(
                card.id,
                getProductOutletGroupStats({
                    outletGroups,
                    priceRows,
                    productId: card.competitorProductId,
                })
            );
        });
        return map;
    }, [skuCards, outletGroups, priceRows]);

    // Default: expand all SKU cards once computed; keep user toggles and expand only truly new cards
    useEffect(() => {
        if (!skuExpandPrefsLoaded) return;
        if (skuCards.length === 0) return;
        setExpandedSkuCardIds((prev) => {
            const next = new Set(prev);
            const currentIds = new Set(skuCards.map((c) => c.id));
            Array.from(next).forEach((id) => {
                if (!currentIds.has(id)) next.delete(id);
            });

            if (prev.size === 0 && savedKnownSkuCardIds === null) {
                skuCards.forEach((c) => next.add(c.id));
                return next;
            }

            if (Array.isArray(savedKnownSkuCardIds)) {
                const knownSet = new Set(savedKnownSkuCardIds);
                skuCards.forEach((c) => {
                    if (!knownSet.has(c.id)) next.add(c.id);
                });
            }
            return next;
        });
    }, [skuCards, skuExpandPrefsLoaded, savedKnownSkuCardIds]);

    // Persist SKU card known snapshot (so collapsed stays collapsed across reloads)
    useEffect(() => {
        if (!skuCardExpandStorageKey) return;
        try {
            localStorage.setItem(
                skuCardExpandStorageKey,
                JSON.stringify({
                    v: 1,
                    expanded: Array.from(expandedSkuCardIds),
                    known: skuCards.map((c) => c.id),
                })
            );
        } catch (e) {
            // ignore
        }
    }, [expandedSkuCardIds, skuCardExpandStorageKey, skuCards]);

    const toggleSkuCard = (cardId: string) => {
        setExpandedSkuCardIds((prev) => {
            const next = new Set(prev);
            if (next.has(cardId)) next.delete(cardId);
            else next.add(cardId);
            return next;
        });
    };

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div
                    style={{
                        width: "100vw",
                        maxWidth: "100vw",
                        marginLeft: "calc(-50vw + 50%)",
                        marginRight: "calc(-50vw + 50%)",
                        paddingLeft: "10rem",
                        paddingRight: "10rem",
                    }}
                >
                    <div style={{ width: "100%", maxWidth: "100%", overflow: "visible" }}>
                        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                            <div className="col-span-12">
                                {/* Filters (positioned top-left under breadcrumb) */}
                                <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center">
                                    {/* Price By (styled like MasterData Filter) */}
                                    <div className="relative inline-flex">
                                        <Lucide
                                            icon="Filter"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        <FormSelect
                                            value={priceBy}
                                            onChange={(e) =>
                                                setPriceBy(e.target.value as PriceBy)
                                            }
                                            className="min-w-[160px] w-auto pl-9 pr-8 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm whitespace-nowrap"
                                        >
                                            <option value="Chain">Price By: Chain</option>
                                            <option value="SKU">Price By: SKU</option>
                                            <option value="Outlet">Price By: Outlet</option>
                                        </FormSelect>
                                    </div>

                                    {/* Date (styled like MasterData Date) */}
                                    <div className="relative inline-flex">
                                        <Lucide
                                            icon="Calendar"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        <Litepicker
                                            value={date}
                                            onChange={(e) => setDate(e.target.value)}
                                            options={{
                                                autoApply: true,
                                                singleMode: true,
                                                showWeekNumbers: false,
                                                dropdowns: {
                                                    minYear: 2020,
                                                    maxYear: null,
                                                    months: true,
                                                    years: true,
                                                },
                                                format: "YYYY-MM-DD",
                                            }}
                                            className="w-fit min-w-[220px] max-w-[320px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Price By: Chain / Outlet */}
                                {(priceBy === "Chain" || priceBy === "Outlet") && (
                                    <div className="mt-6 grid grid-cols-12 gap-x-6 gap-y-6">
                                        {loadingOutletGroups ? (
                                            <div className="col-span-12 flex items-center justify-center py-10">
                                                <div className="text-center">
                                                    <Lucide
                                                        icon="Loader2"
                                                        className="w-7 h-7 animate-spin mx-auto mb-2"
                                                    />
                                                    <div>Loading outlet groups...</div>
                                                </div>
                                            </div>
                                        ) : (
                                            outletGroups.map((g) => (
                                                <div key={g.id} className="col-span-12">
                                                    <div className="p-5 box">
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-base font-medium">
                                                                {g.groupName}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="ml-auto inline-flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                                                onClick={() => toggleOutletGroup(g.id)}
                                                                aria-expanded={expandedOutletGroupIds.has(
                                                                    g.id
                                                                )}
                                                                aria-controls={`outletgroup-${g.id}`}
                                                            >
                                                                <Lucide
                                                                    icon={
                                                                        expandedOutletGroupIds.has(
                                                                            g.id
                                                                        )
                                                                            ? "ChevronUp"
                                                                            : "ChevronDown"
                                                                    }
                                                                    className="w-5 h-5 stroke-[1.3]"
                                                                />
                                                            </button>
                                                        </div>

                                                {/* Table (left) + Chart (right) */}
                                                {expandedOutletGroupIds.has(g.id) && (
                                                <div
                                                    id={`outletgroup-${g.id}`}
                                                    className="mt-4 grid grid-cols-12 gap-x-6 gap-y-6"
                                                >
                                                    {priceBy === "Chain" && (
                                                        <div className="col-span-12 lg:col-span-6">
                                                            <div className="overflow-auto">
                                                                <Table className="border-b border-slate-200/60 text-xs">
                                                                    <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                                                                        <Table.Tr>
                                                                            <Table.Td className="py-2 font-medium bg-slate-50/70 dark:bg-darkmode-700/30">
                                                                                Product
                                                                            </Table.Td>
                                                                            <Table.Td className="py-2 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30">
                                                                                RM
                                                                            </Table.Td>
                                                                            <Table.Td className="py-2 font-medium text-center bg-yellow-50/70 dark:bg-darkmode-700/30">
                                                                                Diff (RM)
                                                                            </Table.Td>
                                                                            <Table.Td className="py-2 font-medium text-center bg-yellow-50/70 dark:bg-darkmode-700/30 leading-tight">
                                                                                <div>Price</div>
                                                                                <div>Index (%)</div>
                                                                            </Table.Td>
                                                                            <Table.Td className="py-2 font-medium text-center bg-danger/5 dark:bg-darkmode-700/30">
                                                                                RM
                                                                            </Table.Td>
                                                                            <Table.Td className="py-2 font-medium bg-danger/5 dark:bg-darkmode-700/30">
                                                                                Competitor
                                                                            </Table.Td>
                                                                        </Table.Tr>
                                                                    </Table.Thead>
                                                                    <Table.Tbody>
                                                                        {!date || !date.trim() ? (
                                                                            <Table.Tr>
                                                                                <Table.Td
                                                                                    className="py-4 text-slate-500"
                                                                                    colSpan={6}
                                                                                >
                                                                                    Please select a date.
                                                                                </Table.Td>
                                                                            </Table.Tr>
                                                                        ) : loadingPrices || loadingProducts ? (
                                                                            <Table.Tr>
                                                                                <Table.Td
                                                                                    className="py-4 text-slate-500"
                                                                                    colSpan={6}
                                                                                >
                                                                                    Loading pricing data...
                                                                                </Table.Td>
                                                                            </Table.Tr>
                                                                        ) : (pricingTableByOutletGroupId.get(g.id) || [])
                                                                              .length === 0 ? (
                                                                            <Table.Tr>
                                                                                <Table.Td
                                                                                    className="py-4 text-slate-500"
                                                                                    colSpan={6}
                                                                                >
                                                                                    No pricing data for this date.
                                                                                </Table.Td>
                                                                            </Table.Tr>
                                                                        ) : (
                                                                            (pricingTableByOutletGroupId.get(
                                                                                g.id
                                                                            ) || []).map((row) => (
                                                                                <Table.Tr
                                                                                    key={`${g.id}_${row.ownProductId}`}
                                                                                    className="[&_td]:border-b [&_td]:border-slate-200/60"
                                                                                >
                                                                                    <Table.Td className="py-2 bg-slate-50/40 dark:bg-darkmode-700/20">
                                                                                        {row.ownProductName}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-2 text-right tabular-nums bg-slate-50/40 dark:bg-darkmode-700/20">
                                                                                        {formatRM(row.ownPrice)}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-2 text-right tabular-nums bg-yellow-50/40 dark:bg-darkmode-700/20">
                                                                                        {formatDiffRM(
                                                                                            row.ownPrice,
                                                                                            row.competitorPrice
                                                                                        )}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-2 text-right tabular-nums bg-yellow-50/40 dark:bg-darkmode-700/20">
                                                                                        {(() => {
                                                                                            const priceIndex =
                                                                                                getPriceIndexPercentValue(
                                                                                                    row.ownPrice,
                                                                                                    row.competitorPrice
                                                                                                );
                                                                                            const isNegative =
                                                                                                priceIndex !== null &&
                                                                                                priceIndex < 0;
                                                                                            return (
                                                                                                <span
                                                                                                    className={
                                                                                                        isNegative
                                                                                                            ? "text-danger"
                                                                                                            : ""
                                                                                                    }
                                                                                                >
                                                                                                    {formatPriceIndexPercent(
                                                                                                        priceIndex
                                                                                                    )}
                                                                                                </span>
                                                                                            );
                                                                                        })()}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-2 text-right tabular-nums bg-danger/5 dark:bg-darkmode-700/20">
                                                                                        {formatRM(
                                                                                            row.competitorPrice
                                                                                        )}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-2 bg-danger/5 dark:bg-darkmode-700/20">
                                                                                        {row.competitorProductName ||
                                                                                            "-"}
                                                                                    </Table.Td>
                                                                                </Table.Tr>
                                                                            ))
                                                                        )}
                                                                    </Table.Tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {priceBy === "Chain" && (
                                                        <div className="col-span-12 lg:col-span-6">
                                                            <div className="h-full min-h-[260px] rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 p-4 overflow-x-hidden">
                                                                {(() => {
                                                                    const rows =
                                                                        pricingTableByOutletGroupId.get(
                                                                            g.id
                                                                        ) || [];
                                                                    if (
                                                                        !date ||
                                                                        !date.trim() ||
                                                                        loadingPrices ||
                                                                        loadingProducts ||
                                                                        rows.length === 0
                                                                    ) {
                                                                        return (
                                                                            <div className="h-full min-h-[188px] flex items-center justify-center text-sm text-slate-500">
                                                                                No chart data
                                                                            </div>
                                                                        );
                                                                    }

                                                                    // Fit chart width to the container (no horizontal scrolling)
                                                                    const chartHeight =
                                                                        getBarChartHeightPx(
                                                                            rows.length
                                                                        );
                                                                    return (
                                                                        <Chart
                                                                            type="bar"
                                                                            width="auto"
                                                                            height={chartHeight}
                                                                            data={buildBarChartData(rows)}
                                                                            options={buildBarChartOptions()}
                                                                        />
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {priceBy === "Outlet" && (
                                                        <div className="col-span-12">
                                                            <div className="overflow-auto">
                                                                <Table className="border-b border-slate-200/60 text-xs table-fixed w-max">
                                                                    {(() => {
                                                                        const groupProducts =
                                                                            pricingTableByOutletGroupId.get(
                                                                                g.id
                                                                            ) || [];
                                                                        const groupOutlets =
                                                                            outletsByGroupName.get(
                                                                                g.groupName
                                                                            ) || [];

                                                                        if (
                                                                            !date ||
                                                                            !date.trim()
                                                                        ) {
                                                                            return (
                                                                                <Table.Tbody>
                                                                                    <Table.Tr>
                                                                                        <Table.Td
                                                                                            className="py-4 text-slate-500"
                                                                                            colSpan={
                                                                                                1 +
                                                                                                groupProducts.length *
                                                                                                    2
                                                                                            }
                                                                                        >
                                                                                            Please select a date.
                                                                                        </Table.Td>
                                                                                    </Table.Tr>
                                                                                </Table.Tbody>
                                                                            );
                                                                        }

                                                                        if (
                                                                            loadingOutlets ||
                                                                            loadingPrices ||
                                                                            loadingProducts
                                                                        ) {
                                                                            return (
                                                                                <Table.Tbody>
                                                                                    <Table.Tr>
                                                                                        <Table.Td
                                                                                            className="py-4 text-slate-500"
                                                                                            colSpan={
                                                                                                1 +
                                                                                                groupProducts.length *
                                                                                                    2
                                                                                            }
                                                                                        >
                                                                                            Loading...
                                                                                        </Table.Td>
                                                                                    </Table.Tr>
                                                                                </Table.Tbody>
                                                                            );
                                                                        }

                                                                        if (groupOutlets.length === 0) {
                                                                            return (
                                                                                <Table.Tbody>
                                                                                    <Table.Tr>
                                                                                        <Table.Td
                                                                                            className="py-4 text-slate-500"
                                                                                            colSpan={
                                                                                                1 +
                                                                                                groupProducts.length *
                                                                                                    2
                                                                                            }
                                                                                        >
                                                                                            No outlets found for this outlet group.
                                                                                        </Table.Td>
                                                                                    </Table.Tr>
                                                                                </Table.Tbody>
                                                                            );
                                                                        }

                                                                        if (groupProducts.length === 0) {
                                                                            return (
                                                                                <Table.Tbody>
                                                                                    <Table.Tr>
                                                                                        <Table.Td
                                                                                            className="py-4 text-slate-500"
                                                                                            colSpan={1}
                                                                                        >
                                                                                            No pricing data for this date.
                                                                                        </Table.Td>
                                                                                    </Table.Tr>
                                                                                </Table.Tbody>
                                                                            );
                                                                        }

                                                                        const getRepPrice = (
                                                                            outletId: string,
                                                                            productId: string | null
                                                                        ) => {
                                                                            if (!productId) return null;
                                                                            const prices =
                                                                                pricesByOutletIdProductId
                                                                                    .get(outletId)
                                                                                    ?.get(productId) || [];
                                                                            return pickModeOrMedian(prices);
                                                                        };

                                                                        return (
                                                                            <>
                                                                                <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                                                                                    <Table.Tr>
                                                                                        <Table.Td
                                                                                            rowSpan={2}
                                                                                            className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px]"
                                                                                        >
                                                                                            Outlet
                                                                                        </Table.Td>
                                                                                        {groupProducts.map(
                                                                                            (p, idx) => (
                                                                                                <Table.Td
                                                                                                    key={`head_${p.ownProductId}`}
                                                                                                    colSpan={2}
                                                                                                    className={[
                                                                                                        "py-2 px-2 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 min-w-[112px] max-w-[112px] whitespace-normal break-words leading-snug",
                                                                                                        idx > 0
                                                                                                            ? "border-l-2 border-slate-200/60 dark:border-darkmode-400"
                                                                                                            : "",
                                                                                                    ].join(" ")}
                                                                                                >
                                                                                                    {p.ownProductName}
                                                                                                </Table.Td>
                                                                                            )
                                                                                        )}
                                                                                    </Table.Tr>
                                                                                    <Table.Tr>
                                                                                        {groupProducts.map(
                                                                                            (p, idx) => (
                                                                                                <React.Fragment
                                                                                                    key={`sub_${p.ownProductId}`}
                                                                                                >
                                                                                                    <Table.Td
                                                                                                        className={[
                                                                                                            "py-2 px-2 font-medium text-center bg-success/10 dark:bg-success/10 whitespace-nowrap min-w-[56px] w-[56px]",
                                                                                                            idx > 0
                                                                                                                ? "border-l-2 border-slate-200/60 dark:border-darkmode-400"
                                                                                                                : "",
                                                                                                        ].join(
                                                                                                            " "
                                                                                                        )}
                                                                                                    >
                                                                                                        RM
                                                                                                    </Table.Td>
                                                                                                    <Table.Td className="py-2 px-2 font-medium text-center bg-white dark:bg-darkmode-700/20 min-w-[56px] w-[56px] whitespace-normal break-words leading-snug text-[10px]">
                                                                                                        {p.competitorProductName ||
                                                                                                            "Competitor"}
                                                                                                    </Table.Td>
                                                                                                </React.Fragment>
                                                                                            )
                                                                                        )}
                                                                                    </Table.Tr>
                                                                                </Table.Thead>
                                                                                <Table.Tbody>
                                                                                    {groupOutlets.map((o) => (
                                                                                        <Table.Tr
                                                                                            key={`out_${g.id}_${o.id}`}
                                                                                            className="[&_td]:border-b [&_td]:border-slate-200/60"
                                                                                        >
                                                                                            <Table.Td className="py-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] truncate">
                                                                                                {o.name}
                                                                                            </Table.Td>
                                                                                            {groupProducts.map(
                                                                                                (p, idx) => {
                                                                                                    const own =
                                                                                                        getRepPrice(
                                                                                                            o.id,
                                                                                                            p.ownProductId
                                                                                                        );
                                                                                                    const comp =
                                                                                                        getRepPrice(
                                                                                                            o.id,
                                                                                                            p.competitorProductId
                                                                                                        );
                                                                                                    const isCompetitorLower =
                                                                                                        own !== null &&
                                                                                                        comp !== null &&
                                                                                                        comp < own;
                                                                                                    return (
                                                                                                        <React.Fragment
                                                                                                            key={`cell_${o.id}_${p.ownProductId}`}
                                                                                                        >
                                                                                                            <Table.Td
                                                                                                                className={[
                                                                                                                    "py-2 px-2 text-right tabular-nums bg-success/10 dark:bg-success/10 min-w-[56px] w-[56px]",
                                                                                                                    idx > 0
                                                                                                                        ? "border-l-2 border-slate-200/60 dark:border-darkmode-400"
                                                                                                                        : "",
                                                                                                                ].join(
                                                                                                                    " "
                                                                                                                )}
                                                                                                            >
                                                                                                                {formatRM(
                                                                                                                    own
                                                                                                                )}
                                                                                                            </Table.Td>
                                                                                                            <Table.Td
                                                                                                                className={[
                                                                                                                    "py-2 px-2 text-right tabular-nums bg-white dark:bg-darkmode-700/20 min-w-[56px] w-[56px]",
                                                                                                                    isCompetitorLower
                                                                                                                        ? "text-danger font-medium"
                                                                                                                        : "",
                                                                                                                ].join(
                                                                                                                    " "
                                                                                                                )}
                                                                                                            >
                                                                                                                {formatRM(
                                                                                                                    comp
                                                                                                                )}
                                                                                                            </Table.Td>
                                                                                                        </React.Fragment>
                                                                                                    );
                                                                                                }
                                                                                            )}
                                                                                        </Table.Tr>
                                                                                    ))}
                                                                                </Table.Tbody>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Price By: SKU */}
                        {priceBy === "SKU" && (
                            <div className="mt-6 grid grid-cols-12 gap-x-6 gap-y-6">
                                {loadingPrices || loadingProducts || loadingOutletGroups ? (
                                    <div className="col-span-12 flex items-center justify-center py-10">
                                        <div className="text-center">
                                            <Lucide
                                                icon="Loader2"
                                                className="w-7 h-7 animate-spin mx-auto mb-2"
                                            />
                                            <div>Loading pricing data...</div>
                                        </div>
                                    </div>
                                ) : skuCards.length === 0 ? (
                                    <div className="col-span-12 text-slate-500">
                                        No SKU pricing data for this date.
                                    </div>
                                ) : (
                                    skuCards.map((card) => (
                                        <div key={card.id} className="col-span-12">
                                            <div className="p-5 box">
                                                <div className="flex items-center gap-3">
                                                    <div className="text-base font-medium">
                                                        {card.title}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="ml-auto inline-flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                                        onClick={() => toggleSkuCard(card.id)}
                                                        aria-expanded={expandedSkuCardIds.has(
                                                            card.id
                                                        )}
                                                        aria-controls={`skucard-${card.id}`}
                                                    >
                                                        <Lucide
                                                            icon={
                                                                expandedSkuCardIds.has(card.id)
                                                                    ? "ChevronUp"
                                                                    : "ChevronDown"
                                                            }
                                                            className="w-5 h-5 stroke-[1.3]"
                                                        />
                                                    </button>
                                                </div>

                                                {expandedSkuCardIds.has(card.id) && (
                                                    <div
                                                        id={`skucard-${card.id}`}
                                                        className="mt-4 flex flex-col gap-6 lg:flex-row"
                                                    >
                                                        {/* 1) Bar chart (~80% width) */}
                                                        <div className="w-full lg:w-[80%]">
                                                            <div className="h-full min-h-[320px] rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 p-4">
                                                                {(() => {
                                                                    const data =
                                                                        buildSkuOutletGroupChartData(
                                                                            {
                                                                                outletGroups,
                                                                                priceRows,
                                                                                ownProductId:
                                                                                    card.ownProductId,
                                                                                competitorProductId:
                                                                                    card.competitorProductId,
                                                                            }
                                                                        );
                                                                    if (!data) {
                                                                        return (
                                                                            <div className="h-full min-h-[288px] flex items-center justify-center text-sm text-slate-500">
                                                                                No chart data
                                                                            </div>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <Chart
                                                                            type="bar"
                                                                            width="auto"
                                                                            height={320}
                                                                            data={data}
                                                                            options={buildSkuOutletGroupChartOptions()}
                                                                        />
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>

                                                        {/* 2) Two stacked white containers */}
                                                        <div className="w-full lg:w-[20%] flex flex-col gap-6">
                                                            <div className="p-4 box">
                                                                <div className="text-base font-medium">
                                                                    {card.ownName}
                                                                </div>
                                                                {(() => {
                                                                    const stats =
                                                                        skuOwnPriceStatsByCardId.get(
                                                                            card.id
                                                                        );
                                                                    const high =
                                                                        stats?.high || null;
                                                                    const low =
                                                                        stats?.low || null;

                                                                    return (
                                                                        <div className="mt-3 space-y-3 text-xs">
                                                                            {/* Highest: icon, outlet group, price */}
                                                                            <div className="grid grid-cols-[18px_1fr_auto] items-center gap-2">
                                                                                <Lucide
                                                                                    icon="ArrowUp"
                                                                                    className="w-4 h-4 text-success"
                                                                                />
                                                                                <div className="min-w-0 truncate text-slate-500">
                                                                                    {high
                                                                                        ? high.outletGroupName
                                                                                        : "-"}
                                                                                </div>
                                                                                <div className="tabular-nums font-medium">
                                                                                    {high
                                                                                        ? `RM ${high.price.toFixed(
                                                                                              2
                                                                                          )}`
                                                                                        : "-"}
                                                                                </div>
                                                                            </div>

                                                                            {/* Lowest: icon, outlet group, price */}
                                                                            <div className="grid grid-cols-[18px_1fr_auto] items-center gap-2">
                                                                                <Lucide
                                                                                    icon="ArrowDown"
                                                                                    className="w-4 h-4 text-danger"
                                                                                />
                                                                                <div className="min-w-0 truncate text-slate-500">
                                                                                    {low
                                                                                        ? low.outletGroupName
                                                                                        : "-"}
                                                                                </div>
                                                                                <div className="tabular-nums font-medium">
                                                                                    {low
                                                                                        ? `RM ${low.price.toFixed(
                                                                                              2
                                                                                          )}`
                                                                                        : "-"}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                            <div className="p-4 box">
                                                                <div className="text-base font-medium">
                                                                    {card.competitorName}
                                                                </div>
                                                                {(() => {
                                                                    const stats =
                                                                        skuCompetitorPriceStatsByCardId.get(
                                                                            card.id
                                                                        );
                                                                    const high =
                                                                        stats?.high || null;
                                                                    const low =
                                                                        stats?.low || null;

                                                                    return (
                                                                        <div className="mt-3 space-y-3 text-xs">
                                                                            {/* Highest: icon, outlet group, price */}
                                                                            <div className="grid grid-cols-[18px_1fr_auto] items-center gap-2">
                                                                                <Lucide
                                                                                    icon="ArrowUp"
                                                                                    className="w-4 h-4 text-success"
                                                                                />
                                                                                <div className="min-w-0 truncate text-slate-500">
                                                                                    {high
                                                                                        ? high.outletGroupName
                                                                                        : "-"}
                                                                                </div>
                                                                                <div className="tabular-nums font-medium">
                                                                                    {high
                                                                                        ? `RM ${high.price.toFixed(
                                                                                              2
                                                                                          )}`
                                                                                        : "-"}
                                                                                </div>
                                                                            </div>

                                                                            {/* Lowest: icon, outlet group, price */}
                                                                            <div className="grid grid-cols-[18px_1fr_auto] items-center gap-2">
                                                                                <Lucide
                                                                                    icon="ArrowDown"
                                                                                    className="w-4 h-4 text-danger"
                                                                                />
                                                                                <div className="min-w-0 truncate text-slate-500">
                                                                                    {low
                                                                                        ? low.outletGroupName
                                                                                        : "-"}
                                                                                </div>
                                                                                <div className="tabular-nums font-medium">
                                                                                    {low
                                                                                        ? `RM ${low.price.toFixed(
                                                                                              2
                                                                                          )}`
                                                                                        : "-"}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}


